import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  createDivisionSchema,
  updateDivisionSchema,
  type CreateDivisionFormData,
  type UpdateDivisionFormData,
} from '@/lib/validations/auth'

interface DivisionFormProps {
  divisionId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type DivisionFormValues = CreateDivisionFormData | UpdateDivisionFormData

export function DivisionForm({ divisionId, onSuccess, onCancel }: DivisionFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!divisionId

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DivisionFormValues>({
    resolver: zodResolver(isEditMode ? updateDivisionSchema : createDivisionSchema),
    defaultValues: {
      descriptions: '',
    },
  })

  const normalizeTitleCase = (value: string): string => {
    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  useEffect(() => {
    if (isEditMode && divisionId) {
      setIsLoading(true)
      apiClient
        .get(`/division-management/divisions/${divisionId}`)
        .then(({ data }) => {
          const divisionData = data?.data || data || {}

          reset({
            name: divisionData.name || '',
            code: divisionData.code || '',
            descriptions: divisionData.descriptions || '',
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [divisionId, isEditMode, reset, showError])

  const onSubmit = async (data: DivisionFormValues) => {
    setIsSubmitting(true)

    try {
      const payload = {
        name: normalizeTitleCase(data.name),
        code: data.code.trim().toUpperCase(),
        descriptions: data.descriptions
          ? normalizeTitleCase(data.descriptions)
          : null,
      }

      if (isEditMode) {
        await apiClient.put(`/division-management/divisions/${divisionId}`, payload)
        showSuccess('Success', 'Division updated successfully')
      } else {
        await apiClient.post('/division-management/divisions', payload)
        showSuccess('Success', 'Division created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/division-management/divisions')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/division-management/divisions')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading division...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Operations, Finance, HR" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The name is how it appears on your site.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.code}>
            <Input {...register('code')} placeholder="e.g. operations, finance, hr" />
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            <FormHelperText>The code is used in URLs. Only letters, numbers, and hyphens.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Enter division description..."
              rows={4}
            />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
            <FormHelperText>Optional description for this division.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Division'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
